import { HandymanPricePage } from '@/index/service-category/handyman/HandymanPricePage'
import { PageAnalytics } from '@/commons/PageAnalytics'
import { getArticleDateTime } from 'utils/article-date-time'
import { loadHandymanCategorySettings, loadHandymanPriceList, formatPrice, priceRange } from 'utils/handyman-price-data'
import { buildElectricianPriceFaqs } from 'utils/faq'
import { electricianMarketRates } from 'data/handyman-market-rates'
import {
  ELECTRICIAN_HUB,
  ELECTRICIAN_PRICE_PAGE,
  HANDYMAN_PRICE_PAGE,
  handymanJobPages,
  jobPagePath,
  jobPagesForHub,
} from 'data/handyman-job-pages'

// Owns "electrician price singapore" and "cheap electrician singapore".
// The hub shows only a short list of popular prices and links here.

// Hand-set: bump when the prices or the market comparison are re-checked.
const LAST_REVIEWED = '2026-09-23T08:00:00+08:00'

export async function getStaticProps() {
  const { year } = getArticleDateTime()
  const handymanPriceList = await loadHandymanPriceList('Singapore')
  const requestCategorySettings = await loadHandymanCategorySettings('Singapore')

  return {
    props: { handymanPriceList, requestCategorySettings, year },
    revalidate: 259200, // In seconds
  }
}

const priceFactors = [
  { heading: 'The job itself', text: 'Replacing a bulb or socket is a short job. Installing a distribution board or repairing wiring takes longer and needs more skill, and is priced accordingly.' },
  { heading: 'Supply and install, or install only', text: 'Power points listed as supply and install include the part. For fans, lights and heaters, you can buy the item yourself or have the electrician supply it at additional cost, agreed before work starts.' },
  { heading: 'Number of points in one visit', text: 'Adding several lights, sockets or fans in one visit avoids paying for repeat trips.' },
  { heading: 'Transport and call-out fees', text: 'Some providers quote a low job price and add a separate transport or call-out fee. Always compare the total.' },
]

const cheapTips = (maxFee) => [
  { heading: 'Compare fixed prices, not hourly rates', text: 'A fixed per-job price cannot overrun if the electrician takes longer than expected.' },
  { heading: 'Check for call-out fees', text: `Some Singapore electricians add a transport or call-out fee of up to $${maxFee} on top of the job price. Ask for the total before you book.` },
  { heading: 'Group electrical jobs together', text: 'If you need a new socket, a light and a fan, book them together so one visit covers everything.' },
  { heading: 'Fix the cause of repeated trips', text: 'Resetting a tripping breaker again and again does not fix anything. Finding the faulty circuit once is cheaper than repeated call-outs.' },
  { heading: 'Use first-booking and referral rewards', text: 'StringsSG gives up to 20% off a first booking, loyalty points on every service and referral rewards for friends and family.' },
]

const relatedLinks = [
  ...jobPagesForHub(ELECTRICIAN_HUB).map((slug) => ({ href: jobPagePath(slug), label: handymanJobPages[slug].breadcrumbTitle })),
  { href: ELECTRICIAN_HUB, label: 'Electrician Services Singapore', desc: 'All electrician services and booking' },
  { href: HANDYMAN_PRICE_PAGE, label: 'Handyman Price List', desc: 'Mounting, curtains, locks, painting and more' },
]

export default function Page({ handymanPriceList, requestCategorySettings, year }) {
  // Everything in the Electrical category, including heater jobs that the API
  // assigns to a handyman rather than an electrician.
  const items = handymanPriceList
    .filter((item) => item.category === 'Electrical')
    .sort((a, b) => a.serviceCost - b.serviceCost)
  const range = priceRange(items)
  const low = formatPrice(range.low)

  const price = (name) => {
    const item = items.find((i) => i.itemName === name)
    return item ? formatPrice(item.serviceCost) : null
  }
  const faqs = buildElectricianPriceFaqs(price, { low, high: formatPrice(range.high) })

  return (
    <>
      <HandymanPricePage
        path={ELECTRICIAN_PRICE_PAGE}
        label="Electrician"
        title={`Electrician Price List Singapore (${year}) | From ${low}`}
        description={`Electrician prices in Singapore: ${items.length} fixed-price jobs from ${low}, typical market rates and tips to get a cheap, licensed electrician.`}
        h1={`Electrician Price List Singapore (${year})`}
        items={items}
        handymanPriceList={handymanPriceList}
        requestCategorySettings={requestCategorySettings}
        marketRates={electricianMarketRates}
        faqs={faqs}
        cheapTips={cheapTips}
        priceFactors={priceFactors}
        relatedLinks={relatedLinks}
        lastReviewed={LAST_REVIEWED}
        image="handyman/electrician-installing-lights.jpg"
        bookingProps={{
          defaultServiceType: 'Electrical',
          allowedServiceTypes: ['Electrical'],
          allowedCategories: ['Electrical'],
        }}
      />
      <PageAnalytics />
    </>
  )
}
